// ============================================
// API TYPES
// ============================================

export interface ApiResponse<T = unknown> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
}

// ============================================
// USER TYPES
// ============================================

export interface User {
  id: string;
  telegramId: number;
  username: string | null;
  firstName: string | null;
  lastName: string | null;
  photoUrl: string | null;
  languageCode: string;
  createdAt: string;
  lastActiveAt: string;
  onboardingCompleted: boolean;
  role: string;
  subscriptionTier: string;
  subscriptionExpiresAt: string | null;

  // V2 profile (12 archetypes)
  primaryArchetype: ArchetypeCode | null;
  secondaryArchetype: ArchetypeCode | null;
  shadowRisk: ArchetypeCode | null;

  // 8 characteristics (0-100)
  charBodyEnergy: number;
  charAttention: number;
  charEmotionalRegulation: number;
  charCommunication: number;
  charTrustBonds: number;
  charAgency: number;
  charCreativity: number;
  charMeaning: number;

  // Stage
  currentStage: number;
  stageActiveDays: number;

  // Battery completion flags
  batteryBCompleted?: boolean;
  batteryCCompleted?: boolean;
}

// ============================================
// CHARACTER TYPES
// ============================================

export type Archetype = 'seeker' | 'creator' | 'guardian' | 'warrior';

export interface Character {
  id: string;
  userId: string;
  name: string | null;
  archetype: Archetype | null;
  secondaryArchetype: Archetype | null;
  isHybrid: boolean;
  level: number;
  totalXp: number;
  currentLevelXp: number;
  avatarUrl: string | null;
  baseMind: number;
  baseBody: number;
  baseConnections: number;
  baseMeaning: number;
  createdAt: string;
  updatedAt: string;
}

// ============================================
// AUTH TYPES
// ============================================

export interface AuthResponse {
  token: string;
  user: User;
  character: Character | null;
  isNewUser: boolean;
}

// ============================================
// DIAGNOSIS TYPES
// ============================================

export type DiagnosisStage = 'intro' | 'situational' | 'associative' | 'reflection' | 'complete';

export interface DiagnosisScores {
  mind: number;
  body: number;
  connections: number;
  meaning: number;
}

export interface DiagnosisMessage {
  role: 'user' | 'assistant';
  content: string;
}

export interface DiagnosisSession {
  id: string;
  currentStage: DiagnosisStage;
  questionIndex: number;
  scores: DiagnosisScores;
}

export interface DaemonResponse {
  message: string;
  sessionId: string;
  stage: DiagnosisStage;
  isComplete: boolean;
  character?: Character;
}

// ============================================
// SKILL TYPES
// ============================================

export type SkillBranch = 'mind' | 'body' | 'connections' | 'meaning';

export interface Skill {
  skillKey: string;
  skillName: string;
  branch: SkillBranch;
  currentLevel: number;
  currentXp: number;
  xpToNextLevel: number;
  unlocked: boolean;
  description: string | null;
}

// ============================================
// ARCHETYPE INFO
// ============================================

export interface ArchetypeInfo {
  name: string;
  emoji: string;
  description: string;
  strengths: string[];
  growthAreas: string[];
  color: string;
}

export const ARCHETYPE_INFO: Record<Archetype, ArchetypeInfo> = {
  seeker: {
    name: 'Искатель',
    emoji: '🔮',
    description: 'Ты ищешь ответы на большие вопросы. Тебе важно понимать смысл происходящего и своё место в мире.',
    strengths: ['Глубокое мышление', 'Философский взгляд', 'Рефлексия'],
    growthAreas: ['Присутствие в теле', 'Действие без анализа', 'Простые радости'],
    color: 'seeker',
  },
  creator: {
    name: 'Творец',
    emoji: '✨',
    description: 'Ты создаёшь новое через взаимодействие с другими. Твоя сила — в способности вдохновлять и объединять.',
    strengths: ['Креативность', 'Коммуникация', 'Вдохновение'],
    growthAreas: ['Завершение начатого', 'Личные границы', 'Время наедине'],
    color: 'creator',
  },
  guardian: {
    name: 'Хранитель',
    emoji: '🛡️',
    description: 'Ты заботишься о себе и других, создаёшь безопасное пространство. Твоя сила — в стабильности и поддержке.',
    strengths: ['Забота', 'Надёжность', 'Эмпатия'],
    growthAreas: ['Забота о себе', 'Принятие перемен', 'Выход из зоны комфорта'],
    color: 'guardian',
  },
  warrior: {
    name: 'Воин',
    emoji: '⚔️',
    description: 'Ты действуешь и преодолеваешь. Твоя сила — в целеустремлённости и способности добиваться результата.',
    strengths: ['Решительность', 'Дисциплина', 'Достижение целей'],
    growthAreas: ['Принятие слабости', 'Замедление', 'Глубокие связи'],
    color: 'warrior',
  },
};

// ============================================
// V2 ARCHETYPE TYPES (12 archetypes)
// ============================================

export type ArchetypeCode =
  | 'INN' | 'EVM' | 'HER' | 'CAR'
  | 'EXP' | 'REB' | 'LOV' | 'CRE'
  | 'JES' | 'SAG' | 'MAG' | 'RUL';

export interface ArchetypeV2Info {
  id: ArchetypeCode;
  displayName: string;
  nameRu: string;
  description: string;
  strengths: string[];
  riskDescription: string;
  emoji: string;
}

export const ARCHETYPE_V2_INFO: Record<ArchetypeCode, ArchetypeV2Info> = {
  INN: {
    id: 'INN',
    displayName: 'Стабилизатор',
    nameRu: 'Невинный',
    description: 'Ты стремишься к гармонии и безопасности. Веришь в лучшее в людях и умеешь создавать атмосферу доверия.',
    strengths: ['Оптимизм', 'Доверие', 'Простота'],
    riskDescription: 'Наивность, избегание проблем, отрицание сложности',
    emoji: '🌿',
  },
  EVM: {
    id: 'EVM',
    displayName: 'Свой человек',
    nameRu: 'Обычный парень',
    description: 'Ты ценишь принадлежность и связи. Хочешь быть частью чего-то большего. Надёжный и эмпатичный.',
    strengths: ['Эмпатия', 'Надёжность', 'Принадлежность'],
    riskDescription: 'Конформизм, потеря индивидуальности, боязнь выделяться',
    emoji: '🤝',
  },
  HER: {
    id: 'HER',
    displayName: 'Воин',
    nameRu: 'Герой',
    description: 'Ты стремишься проявить себя через смелые поступки. Преодолеваешь препятствия и защищаешь слабых.',
    strengths: ['Смелость', 'Решительность', 'Дисциплина'],
    riskDescription: 'Выгорание, агрессивность, неспособность отступить',
    emoji: '⚔️',
  },
  CAR: {
    id: 'CAR',
    displayName: 'Хранитель',
    nameRu: 'Опекун',
    description: 'Ты заботишься о других, щедр и сострадателен. Находишь смысл в помощи и поддержке.',
    strengths: ['Забота', 'Щедрость', 'Сострадание'],
    riskDescription: 'Самопожертвование, контроль через заботу, истощение',
    emoji: '🛡️',
  },
  EXP: {
    id: 'EXP',
    displayName: 'Исследователь',
    nameRu: 'Искатель',
    description: 'Ты жаждешь свободы и новых переживаний. Исследуешь мир и себя, не терпишь ограничений.',
    strengths: ['Любопытство', 'Независимость', 'Аутентичность'],
    riskDescription: 'Бесцельное блуждание, неспособность к обязательствам, бегство',
    emoji: '🧭',
  },
  REB: {
    id: 'REB',
    displayName: 'Разрушитель',
    nameRu: 'Бунтарь',
    description: 'Ты ломаешь правила и устаревшие структуры. Революционер, который видит несправедливость.',
    strengths: ['Честность', 'Свобода', 'Смелость перемен'],
    riskDescription: 'Разрушение без созидания, саморазрушение, изоляция',
    emoji: '🔥',
  },
  LOV: {
    id: 'LOV',
    displayName: 'Связующий',
    nameRu: 'Влюблённый',
    description: 'Ты ценишь близость, красоту и страсть. Создаёшь глубокие связи и наслаждаешься жизнью.',
    strengths: ['Страсть', 'Преданность', 'Глубина отношений'],
    riskDescription: 'Зависимость, потеря себя в отношениях, идеализация',
    emoji: '💜',
  },
  CRE: {
    id: 'CRE',
    displayName: 'Созидатель',
    nameRu: 'Творец',
    description: 'Ты создаёшь новое, воплощаешь видение в реальность. Ценишь самовыражение и мастерство.',
    strengths: ['Воображение', 'Креативность', 'Мастерство'],
    riskDescription: 'Перфекционизм, незавершённость проектов, отрыв от реальности',
    emoji: '✨',
  },
  JES: {
    id: 'JES',
    displayName: 'Трикстер',
    nameRu: 'Шут',
    description: 'Ты привносишь радость и лёгкость. Умеешь видеть абсурд и находить юмор в любой ситуации.',
    strengths: ['Юмор', 'Лёгкость', 'Находчивость'],
    riskDescription: 'Избегание серьёзности, поверхностность, манипуляции через юмор',
    emoji: '🎭',
  },
  SAG: {
    id: 'SAG',
    displayName: 'Аналитик',
    nameRu: 'Мудрец',
    description: 'Ты ищешь истину и понимание. Ценишь знания, рефлексию и глубокий анализ.',
    strengths: ['Мудрость', 'Аналитичность', 'Глубина понимания'],
    riskDescription: 'Отстранённость, паралич анализа, отрыв от чувств',
    emoji: '🔮',
  },
  MAG: {
    id: 'MAG',
    displayName: 'Трансформатор',
    nameRu: 'Маг',
    description: 'Ты видишь скрытые связи и возможности для трансформации. Катализатор изменений.',
    strengths: ['Интуиция', 'Трансформация', 'Видение связей'],
    riskDescription: 'Манипулятивность, мессианство, оторванность от земли',
    emoji: '🌟',
  },
  RUL: {
    id: 'RUL',
    displayName: 'Архитектор',
    nameRu: 'Правитель',
    description: 'Ты создаёшь порядок и структуру. Лидер, который берёт ответственность и строит системы.',
    strengths: ['Лидерство', 'Ответственность', 'Системность'],
    riskDescription: 'Авторитарность, ригидность, страх хаоса',
    emoji: '👑',
  },
};
